/* =========================================================================
 *  PDP Options Modal controller
 *  ----------------------------
 *  Mobile-only bottom-sheet that hosts the variant picker. On page load
 *  we move the existing form `.part-1-area` (colour) and `.part-2-area`
 *  (size) DOM nodes into the modal slot. Event listeners and selectors
 *  are preserved — the variant cascade in the form snippet keeps working
 *  unchanged.
 *
 *  Public behaviour:
 *    - Trigger button (`.pdp-options-trigger`, lives inside the sticky
 *      ATC bar) opens the modal
 *    - Backdrop / Close X / ESC all close the modal
 *      (no Confirm button — the sticky ATC is always live, so the
 *       shopper can adjust qty + add to cart from inside the modal)
 *    - Body scroll locked while open; scroll position preserved on close
 *    - Focus trapped inside the panel; restored to the trigger on close
 *    - Trigger label updates as the user picks options (eg. "Black ·
 *      60cm — change")
 *    - Sticky ATC tap with no size selected → opens the modal first;
 *      subsequent taps with selections present submit as normal
 *    - The sticky ATC bar's measured height is read into the CSS variable
 *      `--fm-pdp-atc-height` so the modal panel sits flush with its top
 *      edge, leaving the bar tappable while the modal is open
 *
 *  Mobile breakpoint: matches the CSS `@media (max-width: 767px)`.
 * ========================================================================= */
(function () {
  'use strict';

  var MOBILE_BREAKPOINT = 767;

  function isMobile() {
    return window.innerWidth <= MOBILE_BREAKPOINT;
  }

  document.addEventListener('DOMContentLoaded', function () {
    var modal = document.querySelector('.pdp-options-modal');
    /* Multiple triggers can exist on the page now (sticky-bar pill +
       inline placeholder where the form's option groups used to sit).
       Both open the same modal and share a single label-sync pipeline. */
    var triggers = document.querySelectorAll('.pdp-options-trigger');
    if (!modal || !triggers.length) return;

    var slot = modal.querySelector('[data-modal-slot]');
    var form = document.querySelector('.custom-multioptions-form');
    if (!form || !slot) return;

    var part1 = form.querySelector(':scope > .part-1-area');
    var part2 = form.querySelector(':scope > .part-2-area');
    /* Custom-dimensions wrapper, only present on the
       `product-calculations-150max` template (Liquid-gated). When it's
       there it carries the width / length inputs and the backing
       radios — moving it into the modal alongside the colour swatches
       so the shopper enters every required option in one place. */
    var calc150 = form.querySelector(':scope > .product-calculations-150max-wrp');
    var stickyBar = form.querySelector(':scope > .sticky-add-to-cart')
                  || document.querySelector('.sticky-add-to-cart');

    /* ----------------------------------------------------------------
     *  Reparenting is REVERSIBLE (03-Aug-2026)
     *  ---------------------------------------
     *  The move below used to be one-shot at DOMContentLoaded with no way
     *  back: the resize listener further down only closed an open modal.
     *  So a session that loaded at <=767px and then widened past it — a
     *  tablet rotated to landscape, a desktop window opened narrow and
     *  dragged wider — was left with the colour swatches and (on the
     *  made-to-measure template) the width / length / backing inputs
     *  stranded inside `.pdp-options-modal`, which is `display: none`
     *  above the breakpoint. The options became unreachable until reload,
     *  with no visible cause.
     *
     *  `restoreToForm` puts each node back at its original index among the
     *  form's children, so the on-page order is exactly what Liquid
     *  rendered rather than whatever order they happen to come back in.
     * ---------------------------------------------------------------- */
    var movedNodes = [];

    function rememberOriginalPosition(node) {
      if (!node) return;
      movedNodes.push({
        node: node,
        parent: node.parentNode,
        nextSibling: node.nextSibling
      });
    }

    function restoreToForm() {
      /* Reverse order so re-inserting before a remembered nextSibling
         cannot land ahead of a sibling that is itself still displaced. */
      for (var i = movedNodes.length - 1; i >= 0; i--) {
        var entry = movedNodes[i];
        if (!entry.parent) continue;
        entry.parent.insertBefore(entry.node, entry.nextSibling);
      }
      movedNodes = [];
      document.body.classList.remove('pdp-options-modal-active');
    }

    function moveToSlot(node) {
      if (!node) return;
      rememberOriginalPosition(node);
      slot.appendChild(node);
    }

    /* ---- Mobile: move form parts into the modal slot at page load ---- */
    if (isMobile()) {
      moveToSlot(part1);
      moveToSlot(part2);
      moveToSlot(calc150);

      /* Move the bulk-savings card into the modal slot too — the
         `--inline` slot lives just outside the form (above the
         description accordion) and on mobile the customer is most
         likely to want to see the tier ladder while picking sizes /
         setting quantity. The collapsible mobile presentation
         (closed-banner with "View price breaks ›") is sized to fit
         comfortably above the qty stepper. The matching `--gallery`
         instance on desktop stays where it is (under the main image),
         so desktop is unchanged. */
      var bulkSlot = document.querySelector('.pdp-bulk-savings-slot--inline');
      moveToSlot(bulkSlot ? bulkSlot.querySelector('.pdp-bulk') : null);
      moveToSlot(bulkSlot ? bulkSlot.querySelector('.pdp-bulk__hint') : null);

      document.body.classList.add('pdp-options-modal-active');
      updateAtcHeightVar();
      applyVariantImagesToSwatches(slot);
    }

    /* ----------------------------------------------------------------
     *  Swap colour swatches to use the variant's featured image
     *  --------------------------------------------------------
     *  The Liquid templates expose `data-variant-image` on every colour
     *  swatch's wrap div. Each swatch's <label> already carries a fallback
     *  background-image (the small circular swatch asset like `red.png`)
     *  via inline `style`. When a variant has its own featured image we
     *  override that background-image inline so the modal's larger
     *  squares preview the actual product photo for that colour. Falls
     *  back silently (no-op) when the data attribute is empty —
     *  preserves the swatch-asset fallback for variants without a
     *  featured image.
     *
     *  Mobile/modal-scoped: only swatches inside the modal slot are
     *  touched; the on-page form (desktop) keeps the original
     *  circular-asset swatch look.
     * ---------------------------------------------------------------- */
    function applyVariantImagesToSwatches(scope) {
      if (!scope) return;
      scope.querySelectorAll('.swatch-element[data-variant-image]').forEach(function (wrap) {
        var url = (wrap.getAttribute('data-variant-image') || '').trim();
        if (!url) return;
        var label = wrap.querySelector('label');
        if (!label) return;
        /* `setProperty` with empty priority lets a !important elsewhere
           in the cascade still win if needed; but the inline style we're
           replacing is itself non-important, so a plain assignment is
           sufficient. */
        label.style.backgroundImage = 'url("' + url + '")';
      });
    }

    /* ----------------------------------------------------------------
     *  Sticky-ATC height → CSS variable
     *  Read once on init, again on resize, and again whenever the bar's
     *  contents could change (eg. ResizeObserver if available).
     * ---------------------------------------------------------------- */
    /* Whichever bar is actually pinned at the bottom of the viewport.
       On the made-to-measure templates `.fm-buybar` replaces the legacy sticky
       bar, which is then `display: none` and measures 0 — and because the
       setter below only accepts a height > 0, the variable kept the LAST good
       reading of the legacy bar. That stale value (133px against a 96px buybar)
       left a 37px strip of page showing between the bottom of the modal panel
       and the top of the bar: the grey band Jack reported, 03-Aug-2026. */
    function bottomBarEl() {
      var buybar = document.querySelector('.fm-buybar');
      if (buybar && getComputedStyle(buybar).display !== 'none') return buybar;
      return stickyBar;
    }

    function updateAtcHeightVar() {
      var el = bottomBarEl();
      if (!el) return;
      var h = el.getBoundingClientRect().height;
      if (h && h > 0) {
        document.documentElement.style.setProperty('--fm-pdp-atc-height', h + 'px');
      }
    }

    /* The buybar changes height when it swaps mode (96px slim → 148px
       selected), and its controller owns that transition — so it tells us
       rather than us polling. */
    document.addEventListener('fm:buybar-resized', updateAtcHeightVar);
    if (window.ResizeObserver && stickyBar) {
      try {
        var ro = new ResizeObserver(updateAtcHeightVar);
        ro.observe(stickyBar);
      } catch (_e) {
        /* fall through to resize listener below */
      }
    }
    window.addEventListener('resize', updateAtcHeightVar);

    /* ----------------------------------------------------------------
     *  Open / Close
     * ---------------------------------------------------------------- */
    var lastFocus = null;
    var savedScrollY = 0;

    function isOpen() {
      return modal.getAttribute('data-state') === 'open';
    }

    function openModal() {
      if (!isMobile() || isOpen()) return;
      lastFocus = document.activeElement;
      savedScrollY = window.scrollY || window.pageYOffset || 0;

      modal.removeAttribute('inert');
      modal.setAttribute('aria-hidden', 'false');
      modal.setAttribute('data-state', 'open');
      document.body.classList.add('pdp-options-modal-open');
      document.body.style.top = '-' + savedScrollY + 'px';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';

      /* Re-measure in case sticky-bar layout changed */
      updateAtcHeightVar();

      /* Move focus into the panel — pick the close button as the safest
         landing spot (it's always present, regardless of option count). */
      var closeBtn = modal.querySelector('.pdp-options-modal__close');
      if (closeBtn) closeBtn.focus({ preventScroll: true });

      document.addEventListener('keydown', onKeydown);
    }

    function closeModal() {
      modal.setAttribute('data-state', 'closed');
      modal.setAttribute('aria-hidden', 'true');
      modal.setAttribute('inert', '');
      document.body.classList.remove('pdp-options-modal-open');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, savedScrollY);

      document.removeEventListener('keydown', onKeydown);

      /* Restore focus to whichever element opened the modal. */
      if (lastFocus && typeof lastFocus.focus === 'function') {
        lastFocus.focus({ preventScroll: true });
      }
    }

    function onKeydown(e) {
      if (e.key === 'Escape' || e.keyCode === 27) {
        e.preventDefault();
        closeModal();
        return;
      }
      if (e.key === 'Tab' || e.keyCode === 9) {
        trapFocus(e);
      }
    }

    /* ---- Focus trap (keep Tab inside the panel while open) ---- */
    function trapFocus(e) {
      var panel = modal.querySelector('.pdp-options-modal__panel');
      if (!panel) return;
      var focusables = panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    /* ----------------------------------------------------------------
     *  Wire up triggers
     * ---------------------------------------------------------------- */
    triggers.forEach(function (t) {
      t.addEventListener('click', openModal);
    });
    modal.querySelectorAll('[data-modal-close]').forEach(function (el) {
      el.addEventListener('click', closeModal);
    });

    /* Leaving the mobile breakpoint while the sheet is open (tablet /
       phone rotated to landscape past 767px) hides the panel via the
       CSS media query but leaves the body scroll-locked
       (position:fixed) with no visible way back — close it properly
       so the page is usable at the new size. (Found 30-Jul-2026 when a
       mid-flow viewport change stranded the sheet.) */
    window.addEventListener('resize', function () {
      if (!isMobile() && isOpen()) closeModal();

      /* Crossing the breakpoint in either direction now re-homes the option
         groups, so neither viewport can be left without them. Both branches
         no-op when there is nothing displaced (`movedNodes` empty) or nothing
         to displace, so this is cheap to run on every resize tick. */
      if (!isMobile()) {
        if (movedNodes.length) restoreToForm();
      } else if (!movedNodes.length) {
        moveToSlot(part1);
        moveToSlot(part2);
        moveToSlot(calc150);
        var lateBulkSlot = document.querySelector('.pdp-bulk-savings-slot--inline');
        moveToSlot(lateBulkSlot ? lateBulkSlot.querySelector('.pdp-bulk') : null);
        moveToSlot(lateBulkSlot ? lateBulkSlot.querySelector('.pdp-bulk__hint') : null);
        document.body.classList.add('pdp-options-modal-active');
        updateAtcHeightVar();
        applyVariantImagesToSwatches(slot);
      }
    });

    /* ----------------------------------------------------------------
     *  Reveal the calculated total once both dimensions are in
     *  ---------------------------------------------------------
     *  On a phone the "Your price" row sits below the backing radios, so
     *  entering the second dimension appeared to do nothing — the total
     *  rendered off-screen, behind the keyboard. `eq-custom.js` fires
     *  `fm:dimensions-complete` on the transition into a priced state; we
     *  scroll the modal body just far enough to bring the row into the
     *  visible area.
     *
     *  Scrolls the modal body specifically, NOT via scrollIntoView, which
     *  walks up to the nearest scrollable ancestor and would drag the page
     *  behind the modal about too. And only ever scrolls DOWN by the shortfall
     *  (`delta > 0`), so a total that is already visible stays put rather than
     *  the view jumping on every keystroke that keeps the pair valid.
     * ---------------------------------------------------------------- */
    document.addEventListener('fm:dimensions-complete', function () {
      if (!isMobile() || !isOpen()) return;

      var body = modal.querySelector('.pdp-options-modal__body');
      var totalMarker = slot.querySelector('.pdp-dims__total');
      if (!body || !totalMarker) return;
      var totalRow = totalMarker.closest('.form__row') || totalMarker;

      /* Short delay so the row has been laid out (it is display:none until
         `.pric_cal` has content) and any keyboard-driven viewport shift has
         settled. setTimeout rather than requestAnimationFrame — rAF does not
         fire in a backgrounded tab, which would silently skip the scroll. */
      setTimeout(function () {
        var bodyRect = body.getBoundingClientRect();
        var rowRect = totalRow.getBoundingClientRect();

        /* With the soft keyboard up, the layout viewport does not shrink on
           iOS — visualViewport is the only thing that knows how much of the
           modal is actually on screen. Its coordinates are relative to the
           layout viewport, matching getBoundingClientRect. */
        var visibleBottom = bodyRect.bottom;
        var vv = window.visualViewport;
        if (vv) {
          visibleBottom = Math.min(visibleBottom, vv.offsetTop + vv.height);
        }

        var delta = rowRect.bottom - visibleBottom + 12; /* 12px breathing room */
        if (delta <= 0) return;

        var reduceMotion = window.matchMedia
          && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (body.scrollBy) {
          body.scrollBy({ top: delta, behavior: reduceMotion ? 'auto' : 'smooth' });
        } else {
          body.scrollTop += delta;
        }
      }, 60);
    });

    /* Sticky ATC tap with no selection → open modal instead of erroring.
       Only intercept if the modal is currently CLOSED — otherwise let
       the form's own ATC validation run (so the user sees the error
       inside the modal). */
    var atcButton = document.querySelector('.custom-atc-basket');
    if (atcButton) {
      atcButton.addEventListener(
        'click',
        function (e) {
          if (!isMobile()) return;
          if (isOpen()) return; /* modal already open — don't re-open */

          var hasSize = !!document.querySelector('.select_ul .selected[data-variant-id]')
                       || !!document.querySelector('input[type="radio"][name="option-2"]:checked');
          var hasColour = !!document.querySelector('input[type="radio"][name="option-1"]:checked');
          var needsColour = !!document.querySelector('input[type="radio"][name="option-1"]');
          var needsSize = !!document.querySelector('.select_ul li[data-variant-id]')
                         || !!document.querySelector('input[type="radio"][name="option-2"]');

          var missing = (needsColour && !hasColour) || (needsSize && !hasSize);
          if (missing) {
            /* `stopImmediatePropagation` (not just `stopPropagation`) is
               required here because the form snippet's own ATC click
               handler is bound to the same element. `stopPropagation`
               would only block parent-element listeners; we need to
               block sibling listeners on the same element too. */
            e.preventDefault();
            e.stopImmediatePropagation();
            openModal();
          }
        },
        true /* capture: run before the form's own ATC click handler */
      );
    }

    /* ----------------------------------------------------------------
     *  Sync the trigger button label with the current selection
     *
     *  Builds a label like "Black · 60cm × 90cm — change" so the shopper
     *  always knows what they've picked even when the modal is closed.
     *  Listens to colour radio change + size <li> click — the same events
     *  the form's own variant cascade hooks into. We defer the read by
     *  one tick so the cascade's text mutations (`.default_option` content,
     *  `.selected` class adds) have landed before we sample state.
     * ---------------------------------------------------------------- */
    function buildSummary() {
      var parts = [];
      var checkedColour = document.querySelector('input[type="radio"][name="option-1"]:checked');
      if (checkedColour && checkedColour.value) {
        parts.push(checkedColour.value);
      }
      var defaultOption = document.querySelector('.default_option .size__text');
      var sizeLi = document.querySelector('.select_ul li.selected');
      var radioSize = document.querySelector('input[type="radio"][name="option-2"]:checked');
      if (sizeLi && defaultOption) {
        var txt = defaultOption.textContent && defaultOption.textContent.trim();
        if (txt && txt !== 'Select Sizes Here') parts.push(txt);
      } else if (radioSize) {
        parts.push(radioSize.value);
      }
      return parts.length ? parts.join(' · ') : null;
    }

    function refreshTriggerLabel() {
      /* `— change` suffix retired (testing showed it added clutter
         without changing behaviour — shoppers already know the
         trigger is tappable). When a selection exists, show just the
         summary (e.g. "Black · 60cm × 90cm"); otherwise the default
         "Select Colour & Size" prompt. */
      var summary = buildSummary();
      var nextText = summary || 'Select Colour & Size';
      triggers.forEach(function (t) {
        var label = t.querySelector('.pdp-options-trigger__label');
        if (label) label.textContent = nextText;
      });
    }

    function deferredRefresh() {
      setTimeout(refreshTriggerLabel, 0);
    }

    document.addEventListener('change', function (e) {
      if (e.target && e.target.matches && e.target.matches('input[type="radio"][name^="option-"]')) {
        deferredRefresh();
      }
    });
    document.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('.select_ul li')) {
        deferredRefresh();
      }
      /* Also catch label clicks on colour swatches — the form's own
         label-click handler dispatches a 'change' event, but that fires
         on the radio so the `change` listener above already captures
         it. This branch is just a belt-and-braces for any future
         non-radio swatch UI. */
      if (e.target && e.target.closest && e.target.closest('.color_swatch_contaner')) {
        deferredRefresh();
      }
    });

    /* Initial pass so the trigger reflects any default-selected variant. */
    refreshTriggerLabel();
  });
})();
